import React, { useEffect, useMemo, useState } from 'react';
import {
  Card, Row, Col, Form, Button, Badge, Modal, Table, Spinner, Alert
} from 'react-bootstrap';
import {
  FaMobileAlt, FaLock, FaUnlock, FaSearch, FaSyncAlt, FaShieldAlt,
  FaDesktop, FaNetworkWired, FaBan, FaCheckCircle, FaSort, FaSortUp,
  FaSortDown, FaTimesCircle, FaHourglassHalf
} from 'react-icons/fa';
import api from '../services/api';
import socket from '../socket_';

// Même libellé que DEVICE_PENDING_REASON dans controllers/authController.js
const DEVICE_PENDING_REASON = 'En attente de validation';
const isPending = (d) => d.is_blocked && d.blocked_reason === DEVICE_PENDING_REASON;

const ROLE_COLORS = {
  admin: '#dc3545',
  responsable: '#b45309',
  pointeur: '#0d6efd',
  responsable_budget: '#6f42c1',
  achat: '#0d9488'
};

const SortIcon = ({ col, sortConfig }) => {
  if (sortConfig.key !== col) return <FaSort className="ms-1 opacity-25" size={10} />;
  return sortConfig.dir === 'asc'
    ? <FaSortUp className="ms-1 text-primary" size={10} />
    : <FaSortDown className="ms-1 text-primary" size={10} />;
};

const KpiCard = ({ icon, label, value, color, sub }) => (
  <Card className="shadow-sm border-0 h-100" style={{ borderLeft: `4px solid ${color}` }}>
    <Card.Body className="d-flex align-items-center gap-3 py-3">
      <div style={{
        width: 44, height: 44, borderRadius: 10,
        background: color + '18',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
      }}>
        {React.cloneElement(icon, { size: 20, color })}
      </div>
      <div>
        <div className="text-muted" style={{ fontSize: '0.78rem' }}>{label}</div>
        <div className="fw-bold" style={{ fontSize: '1.6rem', color, lineHeight: 1.1 }}>{value}</div>
        {sub && <div className="text-muted" style={{ fontSize: '0.72rem' }}>{sub}</div>}
      </div>
    </Card.Body>
  </Card>
);

const UserDevicesPage = () => {
  const [devices, setDevices]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError]               = useState('');
  const [success, setSuccess]           = useState('');

  const [blockedIps, setBlockedIps]     = useState([]);
  const [ipsLoading, setIpsLoading]     = useState(false);
  const [newBlockIp, setNewBlockIp]     = useState('');

  const [search, setSearch]             = useState('');
  const [blockedFilter, setBlockedFilter] = useState('all');
  const [trustedFilter, setTrustedFilter] = useState('all');

  const [sortConfig, setSortConfig]     = useState({ key: 'last_seen_at', dir: 'desc' });

  const [showBlockModal, setShowBlockModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [blockReason, setBlockReason]   = useState('');

  /* ── chargement ─────────────────────────────── */
  const loadDevices = async () => {
    try {
      setLoading(true);
      setError('');
      const { data } = await api.get('/user_devices');
      setDevices(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du chargement des devices');
    } finally {
      setLoading(false);
    }
  };

  const loadBlockedIps = async () => {
    try {
      setIpsLoading(true);
      const { data } = await api.get('/users/blocked-ips');
      setBlockedIps(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('❌ Erreur chargement IPs bloquées :', err);
    } finally {
      setIpsLoading(false);
    }
  };

  useEffect(() => { loadDevices(); loadBlockedIps(); }, []); // eslint-disable-line

  // Nouvel appareil en attente (ou validé ailleurs) → recharger la liste
  useEffect(() => {
    socket.on('devices:pending-changed', loadDevices);
    return () => socket.off('devices:pending-changed', loadDevices);
  }, []); // eslint-disable-line

  /* ── actions IP ──────────────────────────────── */
  const handleUnblockIp = async (ip) => {
    try {
      setActionLoading(true); setError(''); setSuccess('');
      await api.post('/users/reset-rate-limit', { ip });
      setSuccess(`IP ${ip} débloquée`);
      await loadBlockedIps();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur déblocage IP');
    } finally { setActionLoading(false); }
  };

  const handleBlockIp = async () => {
    const ip = newBlockIp.trim();
    if (!ip) return;
    try {
      setActionLoading(true); setError(''); setSuccess('');
      await api.post('/users/block-ip', { ip });
      setSuccess(`IP ${ip} bloquée`);
      setNewBlockIp('');
      await loadBlockedIps();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur blocage IP');
    } finally { setActionLoading(false); }
  };

  /* ── actions device ──────────────────────────── */
  const openBlockModal = (device) => {
    setSelectedDevice(device);
    setBlockReason(device?.blocked_reason || '');
    setShowBlockModal(true);
  };

  const handleBlock = async () => {
    if (!selectedDevice) return;
    try {
      setActionLoading(true); setError(''); setSuccess('');
      await api.patch(`/user_devices/${selectedDevice.id}/block`, { reason: blockReason });
      setSuccess('Device bloqué avec succès');
      setShowBlockModal(false); setSelectedDevice(null); setBlockReason('');
      await loadDevices();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du blocage du device');
    } finally { setActionLoading(false); }
  };

  const handleUnblock = async (device) => {
    const action = isPending(device) ? 'Valider' : 'Débloquer';
    if (!window.confirm(`${action} "${device.device_name || device.device_id}" (${device.user_name || device.user_login || 'utilisateur'}) ?`)) return;
    try {
      setActionLoading(true); setError(''); setSuccess('');
      await api.patch(`/user_devices/${device.id}/unblock`);
      setSuccess(isPending(device) ? 'Appareil validé avec succès' : 'Device débloqué avec succès');
      await loadDevices();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du déblocage du device');
    } finally { setActionLoading(false); }
  };

  /* ── KPIs ────────────────────────────────────── */
  const stats = useMemo(() => {
    const total   = devices.length;
    const blocked = devices.filter(d => d.is_blocked).length;
    const trusted = devices.filter(d => d.is_trusted).length;
    const pending = devices.filter(isPending).length;
    const active  = total - blocked;
    return { total, blocked, trusted, active, pending };
  }, [devices]);

  const pct = (n) => stats.total > 0 ? `${Math.round(n / stats.total * 100)} %` : '';

  /* ── tri + filtre côté client ────────────────── */
  const handleSort = (key) => {
    setSortConfig(prev =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    );
  };

  const filteredAndSorted = useMemo(() => {
    let rows = [...devices];

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(d =>
        [d.user_name, d.user_login, d.device_name, d.device_id,
          d.platform, d.ip_address, d.language, d.timezone]
          .some(v => v && String(v).toLowerCase().includes(q))
      );
    }

    if (blockedFilter === 'pending') rows = rows.filter(isPending);
    if (blockedFilter === 'true')  rows = rows.filter(d => d.is_blocked);
    if (blockedFilter === 'false') rows = rows.filter(d => !d.is_blocked);
    if (trustedFilter === 'true')  rows = rows.filter(d => d.is_trusted);
    if (trustedFilter === 'false') rows = rows.filter(d => !d.is_trusted);

    const { key, dir } = sortConfig;
    rows.sort((a, b) => {
      let va = a[key] ?? '';
      let vb = b[key] ?? '';

      if (key === 'last_seen_at' || key === 'first_login_at') {
        va = va ? new Date(va).getTime() : 0;
        vb = vb ? new Date(vb).getTime() : 0;
        return dir === 'asc' ? va - vb : vb - va;
      }
      if (typeof va === 'boolean') {
        const n = va === vb ? 0 : va ? 1 : -1;
        return dir === 'asc' ? n : -n;
      }
      if (typeof va === 'number') return dir === 'asc' ? va - vb : vb - va;
      va = String(va).toLowerCase();
      vb = String(vb).toLowerCase();
      return dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });

    return rows;
  }, [devices, search, blockedFilter, trustedFilter, sortConfig]);

  const resetFilters = () => { setSearch(''); setBlockedFilter('all'); setTrustedFilter('all'); };
  const hasFilter = search || blockedFilter !== 'all' || trustedFilter !== 'all';

  /* ── helper ──────────────────────────────────── */
  const formatDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    return isNaN(d) ? '—' : d.toLocaleString('fr-FR');
  };

  const Th = ({ col, children, style }) => (
    <th
      onClick={() => handleSort(col)}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...style }}
    >
      {children}<SortIcon col={col} sortConfig={sortConfig} />
    </th>
  );

  /* ══════════════════════════════════════════════ */
  return (
    <div className="container mt-4">

      {/* HEADER */}
      <Card className="shadow-sm border-0 mb-4">
        <Card.Body className="py-3" style={{ background: 'linear-gradient(135deg,#fff 0%,#f8f9fa 100%)' }}>
          <Row className="align-items-center">
            <Col md={8}>
              <h3 className="mb-1 d-flex align-items-center gap-2">
                <span style={{
                  width: 38, height: 38, borderRadius: 10, background: '#6d4e3620',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <FaMobileAlt size={18} style={{ color: '#6d4e36' }} />
                </span>
                Gestion des Devices
              </h3>
              <p className="text-muted mb-0 small">
                Suivi des appareils connectés · blocage / déblocage · IPs rate-limitées
              </p>
            </Col>
            <Col md={4} className="text-md-end mt-3 mt-md-0">
              <Button variant="outline-secondary" size="sm"
                onClick={() => { loadDevices(); loadBlockedIps(); }} disabled={loading}>
                <FaSyncAlt className="me-2" />Actualiser
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* KPI CARDS */}
      <Row className="mb-4 g-3">
        <Col md xs={6}>
          <KpiCard icon={<FaMobileAlt />} label="Total devices" value={stats.total}   color="#6d4e36" />
        </Col>
        <Col md xs={6}>
          <KpiCard icon={<FaCheckCircle />} label="Actifs"       value={stats.active}  color="#2e7d32" sub={pct(stats.active)} />
        </Col>
        <Col md xs={6}>
          <KpiCard icon={<FaBan />}         label="Bloqués"      value={stats.blocked} color="#c62828" sub={pct(stats.blocked)} />
        </Col>
        <Col md xs={6}>
          <KpiCard icon={<FaShieldAlt />}   label="Trusted"      value={stats.trusted} color="#1565c0" sub={pct(stats.trusted)} />
        </Col>
        <Col md xs={6}>
          <KpiCard icon={<FaHourglassHalf />} label="En attente" value={stats.pending} color="#ef6c00" />
        </Col>
      </Row>

      {/* FILTRES */}
      <Card className="shadow-sm border-0 mb-4">
        <Card.Body className="py-3">
          <Row className="g-2 align-items-end">
            <Col md={5}>
              <div className="input-group input-group-sm">
                <span className="input-group-text"><FaSearch /></span>
                <Form.Control size="sm" type="text"
                  placeholder="Nom, login, device, plateforme, IP..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                {search && (
                  <Button variant="outline-secondary" size="sm" onClick={() => setSearch('')}>
                    <FaTimesCircle />
                  </Button>
                )}
              </div>
            </Col>
            <Col md={3}>
              <Form.Select size="sm" value={blockedFilter} onChange={e => setBlockedFilter(e.target.value)}>
                <option value="all">Tous les statuts</option>
                <option value="pending">En attente de validation</option>
                <option value="true">Bloqués uniquement</option>
                <option value="false">Non bloqués</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select size="sm" value={trustedFilter} onChange={e => setTrustedFilter(e.target.value)}>
                <option value="all">Trusted : tous</option>
                <option value="true">Trusted uniquement</option>
                <option value="false">Non trusted</option>
              </Form.Select>
            </Col>
            <Col md={1} className="text-end">
              <Button size="sm" variant="outline-secondary" onClick={resetFilters} disabled={!hasFilter}>
                Reset
              </Button>
            </Col>
          </Row>
          {hasFilter && (
            <div className="mt-2 text-muted small">
              {filteredAndSorted.length} résultat{filteredAndSorted.length > 1 ? 's' : ''} sur {devices.length}
            </div>
          )}
        </Card.Body>
      </Card>

      {error   && <Alert variant="danger"  dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      {/* TABLEAU DEVICES */}
      <Card className="shadow-sm border-0">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <div className="mt-3 text-muted">Chargement des devices...</div>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle mb-0" style={{ fontSize: '0.84rem' }}>
                <thead style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <tr>
                    <Th col="id">#</Th>
                    <Th col="user_name">Utilisateur</Th>
                    <Th col="device_name">Device</Th>
                    <Th col="platform">Plateforme</Th>
                    <Th col="language">Langue / TZ</Th>
                    <Th col="ip_address">IP</Th>
                    <Th col="is_blocked">Statut</Th>
                    <Th col="first_login_at">1ère connexion</Th>
                    <Th col="last_seen_at">Dernière activité</Th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSorted.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="text-center text-muted py-5">
                        <FaMobileAlt size={32} className="mb-2 d-block mx-auto opacity-25" />
                        Aucun device trouvé
                      </td>
                    </tr>
                  ) : (
                    filteredAndSorted.map(device => (
                      <tr key={device.id} style={device.is_blocked ? { background: '#fff5f5' } : {}}>
                        <td className="text-muted">{device.id}</td>

                        <td>
                          <div className="fw-semibold">{device.user_name || '—'}</div>
                          <div className="d-flex align-items-center gap-1 mt-1 flex-wrap">
                            <span className="text-muted small">{device.user_login || '—'}</span>
                            {device.user_role && (
                              <span style={{
                                fontSize: '0.68rem', padding: '1px 5px', borderRadius: 4,
                                background: (ROLE_COLORS[device.user_role] || '#6c757d') + '20',
                                color: ROLE_COLORS[device.user_role] || '#6c757d',
                                fontWeight: 600
                              }}>
                                {device.user_role}
                              </span>
                            )}
                          </div>
                        </td>

                        <td>
                          <div className="fw-semibold d-flex align-items-center gap-1">
                            <FaDesktop size={12} className="text-muted flex-shrink-0" />
                            {device.device_name || <span className="text-muted fst-italic">Sans nom</span>}
                          </div>
                          {device.screen_resolution && (
                            <div className="text-muted small">{device.screen_resolution}</div>
                          )}
                        </td>

                        <td>
                          <div>{device.platform || '—'}</div>
                          {device.user_agent && (
                            <div className="text-muted small text-truncate" style={{ maxWidth: 160 }}
                              title={device.user_agent}>
                              {device.user_agent}
                            </div>
                          )}
                        </td>

                        <td>
                          <div>{device.language || '—'}</div>
                          <div className="text-muted small">{device.timezone || '—'}</div>
                        </td>

                        <td>
                          <code style={{ fontSize: '0.8rem' }}>{device.ip_address || '—'}</code>
                        </td>

                        <td>
                          <div className="d-flex flex-column gap-1">
                            {isPending(device)
                              ? <Badge bg="warning" text="dark" style={{ fontSize: '0.72rem' }}><FaHourglassHalf className="me-1" />En attente</Badge>
                              : device.is_blocked
                              ? <Badge bg="danger"    style={{ fontSize: '0.72rem' }}>Bloqué</Badge>
                              : <Badge bg="success"   style={{ fontSize: '0.72rem' }}>Actif</Badge>}
                            {device.is_trusted
                              ? <Badge bg="primary"   style={{ fontSize: '0.72rem' }}><FaShieldAlt className="me-1" />Trusted</Badge>
                              : <Badge bg="secondary" style={{ fontSize: '0.72rem' }}>Non trusted</Badge>}
                            {device.blocked_reason && !isPending(device) && (
                              <span className="text-danger fst-italic" style={{ fontSize: '0.72rem' }}>
                                {device.blocked_reason}
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="text-muted small" style={{ whiteSpace: 'nowrap' }}>
                          {formatDate(device.first_login_at)}
                        </td>

                        <td className="text-muted small" style={{ whiteSpace: 'nowrap' }}>
                          {formatDate(device.last_seen_at)}
                        </td>

                        <td>
                          {!device.is_blocked ? (
                            <Button size="sm" variant="outline-danger"
                              onClick={() => openBlockModal(device)} disabled={actionLoading}>
                              <FaLock className="me-1" />Bloquer
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline-success"
                              onClick={() => handleUnblock(device)} disabled={actionLoading}>
                              {isPending(device)
                                ? <><FaCheckCircle className="me-1" />Valider</>
                                : <><FaUnlock className="me-1" />Débloquer</>}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
        {!loading && filteredAndSorted.length > 0 && (
          <Card.Footer className="text-muted small bg-white border-top py-2">
            {filteredAndSorted.length} device{filteredAndSorted.length > 1 ? 's' : ''} affiché{filteredAndSorted.length > 1 ? 's' : ''}
          </Card.Footer>
        )}
      </Card>

      {/* IPS BLOQUÉES */}
      <Card className="shadow-sm border-0 mt-4">
        <Card.Header className="bg-white border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2 py-3">
          <div>
            <h6 className="mb-0 d-flex align-items-center gap-2" style={{ color: '#c62828' }}>
              <FaNetworkWired />IPs bloquées (rate limiter)
            </h6>
            <div className="text-muted small mt-1">
              Blocage auto après 5 tentatives · fenêtre 5 min · admins exemptés
            </div>
          </div>
          <Button variant="outline-secondary" size="sm" onClick={loadBlockedIps} disabled={ipsLoading}>
            <FaSyncAlt className="me-1" />Actualiser
          </Button>
        </Card.Header>
        <Card.Body>
          <div className="d-flex gap-2 mb-3 flex-wrap">
            <input type="text" className="form-control form-control-sm" style={{ maxWidth: 220 }}
              placeholder="IP à bloquer (ex: 192.168.1.10)"
              value={newBlockIp}
              onChange={e => setNewBlockIp(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleBlockIp()}
            />
            <Button size="sm" variant="danger" onClick={handleBlockIp}
              disabled={actionLoading || !newBlockIp.trim()}>
              <FaBan className="me-1" />Bloquer
            </Button>
          </div>

          {ipsLoading ? (
            <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
          ) : blockedIps.length === 0 ? (
            <div className="text-center text-muted py-3 small">Aucune IP bloquée en ce moment</div>
          ) : (
            <div className="table-responsive">
              <Table bordered hover size="sm" className="align-middle mb-0">
                <thead style={{ background: '#f8f9fa' }}>
                  <tr>
                    <th>Adresse IP</th>
                    <th>Type</th>
                    <th>Bloquée depuis</th>
                    <th>Expiration</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {blockedIps.map(entry => (
                    <tr key={entry.ip}>
                      <td><code>{entry.ip}</code></td>
                      <td>
                        {entry.manual
                          ? <Badge bg="danger">Manuel</Badge>
                          : <Badge bg="warning" text="dark">Rate limit</Badge>}
                      </td>
                      <td className="small">
                        {entry.blockedAt ? new Date(entry.blockedAt).toLocaleString('fr-FR') : '—'}
                      </td>
                      <td className="small">
                        {entry.manual || !entry.resetAt
                          ? <span className="text-danger fw-bold">Permanent</span>
                          : new Date(entry.resetAt).toLocaleString('fr-FR')}
                      </td>
                      <td>
                        <Button size="sm" variant="outline-success"
                          onClick={() => handleUnblockIp(entry.ip)} disabled={actionLoading}>
                          <FaUnlock className="me-1" />Débloquer
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* MODAL BLOCAGE */}
      <Modal show={showBlockModal} onHide={() => setShowBlockModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Bloquer un device</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2"><strong>Utilisateur :</strong> {selectedDevice?.user_name || '—'}</p>
          <p className="mb-3"><strong>Device :</strong> {selectedDevice?.device_name || selectedDevice?.device_id || '—'}</p>
          <Form.Group>
            <Form.Label>Motif de blocage</Form.Label>
            <Form.Control as="textarea" rows={3} value={blockReason}
              onChange={e => setBlockReason(e.target.value)}
              placeholder="Ex : appareil perdu, ancien PC, activité suspecte..." />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBlockModal(false)}>Annuler</Button>
          <Button variant="danger" onClick={handleBlock} disabled={actionLoading}>
            <FaLock className="me-2" />Confirmer le blocage
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default UserDevicesPage;
